<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    /** A fixed tab bar sits at the bottom below md; the layout makes room for it there. */
    hasBottomNav?: boolean
    surface?: 'default' | 'transparent'
    fluid?: boolean
  }>(),
  {
    hasBottomNav: false,
    surface: 'default',
    fluid: false,
  },
)

const colorMode = useColorMode()
const isDarkTheme = computed(() => colorMode.value === 'dark')

const isTransparent = computed(() => props.surface === 'transparent')

// Match html/body bg to surface so overscroll bounce doesn't flash white
const htmlBg = computed(() => {
  if (isTransparent.value)
    return isDarkTheme.value ? '#090b11' : '#f4efe7'
  return isDarkTheme.value ? '#101319' : '#f4efe7'
})
useHead({
  htmlAttrs: { style: computed(() => `background-color:${htmlBg.value}`) },
})

// The bottom-nav rules are max-md only: the same layout serves desktop, where there is no
// tab bar to make room for.
const rootClasses = computed(() => [
  isTransparent.value ? 'flex flex-col' : props.hasBottomNav ? 'max-md:flex max-md:flex-col' : '',
  props.surface === 'default'
    ? 'bg-linear-to-b from-paper to-paper-2 text-ink'
    : 'app-layout--transparent text-inherit',
])

const mainClasses = computed(() => [
  props.fluid
    ? 'w-full px-0'
    : 'mx-auto w-full max-w-6xl px-4 sm:px-6',
  // Below md the page footer makes room for the tab bar, so main keeps only a small gap above it.
  isTransparent.value
    ? 'flex-1'
    : props.hasBottomNav
      ? 'max-md:flex-1 max-md:overflow-y-auto max-md:pb-[calc(0.75rem+env(safe-area-inset-bottom))]'
      : '',
])

// The page footer is the last thing on the page, so below md it makes its own room for the
// fixed tab bar (h-12) rather than sitting underneath it.
const pageFooterClasses = computed(() =>
  props.hasBottomNav ? 'max-md:pb-[calc(3rem+env(safe-area-inset-bottom))]' : '',
)
</script>

<template>
  <div class="app-layout min-h-screen" :class="rootClasses">
    <slot name="header" />

    <main id="main" class="app-layout-main" :class="mainClasses">
      <slot />
    </main>

    <div v-if="$slots['page-footer']" :class="pageFooterClasses">
      <slot name="page-footer" />
    </div>

    <slot name="footer" />
  </div>
</template>
